import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from './apiUtils';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from './components/ui/table';
import { Check, IndianRupee, Wallet, Calendar as CalendarIcon } from 'lucide-react';
import { Alert, AlertDescription } from './components/ui/alert';
import { Calendar } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { format } from 'date-fns';

// Helper function to get business date (day starts at 11:30 AM IST)
function getBusinessDate() {
  const now = new Date();
  
  // Check if browser is already in IST timezone
  const browserTimezoneOffset = now.getTimezoneOffset();
  const istTimezoneOffset = -330; // IST is UTC+5:30, so offset is -330 minutes
  
  let istTime;
  if (browserTimezoneOffset === istTimezoneOffset) {
    // Browser is already in IST (local machine), use current time
    istTime = now;
  } else {
    // Browser is in UTC or other timezone, convert to IST
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    istTime = new Date(now.getTime() + istOffset);
  }
  
  if (istTime.getHours() < 11 || (istTime.getHours() === 11 && istTime.getMinutes() < 30)) {
    // Before 11:30 AM IST - use previous day
    const yesterday = new Date(istTime);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toLocaleDateString('en-CA');
  } else {
    // After 11:30 AM IST - use current day
    return istTime.toLocaleDateString('en-CA');
  }
}

function TrackPayments() {
  const [selectedDate, setSelectedDate] = useState(getBusinessDate());
  const [currentBusinessDate, setCurrentBusinessDate] = useState(getBusinessDate());
  
  // Check if selected date is a past date (before current business date)
  const isPastDate = selectedDate < currentBusinessDate;
  
  const [payments, setPayments] = useState({
    cash_amount: '',
    upi_amount: '',
    card_amount: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [summaryData, setSummaryData] = useState({
    todaysSale: 0,
    totalIncome: 0,
    totalExpenses: 0,
    closingBalance: 0
  });
  const [openingBalance, setOpeningBalance] = useState('');
  const [paymentsModified, setPaymentsModified] = useState(true); // Start as true, will be set to false if data exists
  // Removed manual balance setting - now automatically uses previous day's closing balance

  // Monitor business date changes and auto-update selected date
  useEffect(() => {
    const checkBusinessDate = () => {
      const newBusinessDate = getBusinessDate();
      if (newBusinessDate !== currentBusinessDate) {
        setCurrentBusinessDate(newBusinessDate);
        // Only auto-update if user is still on the previous business date
        if (selectedDate === currentBusinessDate) {
          setSelectedDate(newBusinessDate);
        }
      }
    };

    // Check immediately
    checkBusinessDate();
    
    // Check every minute for business date changes
    const interval = setInterval(checkBusinessDate, 60000);
    
    return () => clearInterval(interval);
  }, [currentBusinessDate, selectedDate]);

  useEffect(() => {
    console.log(`🔄 TrackPayments: Date changed to ${selectedDate}, loading data...`);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    const cash = parseFloat(payments.cash_amount) || 0;
    const upi = parseFloat(payments.upi_amount) || 0;
    const card = parseFloat(payments.card_amount) || 0;
    const total = cash + upi + card;
    setTotalAmount(total);
    
    // Calculate closing counter balance
    const opening = parseFloat(openingBalance) || 0;
    const closingBalance = opening + summaryData.todaysSale + summaryData.totalIncome - summaryData.totalExpenses - total;
    setSummaryData(prev => ({ ...prev, closingBalance }));
  }, [payments, openingBalance, summaryData.todaysSale, summaryData.totalIncome, summaryData.totalExpenses]);

  const loadData = async () => {
    try {
      // Load payments data
      const paymentsResponse = await apiGet(`/api/payments?date=${selectedDate}`);
      const paymentsData = await paymentsResponse.json();
      if (paymentsData.payment) {
        setPayments({
          cash_amount: paymentsData.payment.cash_amount.toString(),
          upi_amount: paymentsData.payment.upi_amount.toString(),
          card_amount: paymentsData.payment.card_amount.toString()
        });
        setPaymentsModified(false); // Data exists, not modified
      } else {
        setPayments({
          cash_amount: '',
          upi_amount: '',
          card_amount: ''
        });
        setPaymentsModified(true); // No data exists, treat as modified to show "Save"
      }

      // Load summary data (sales, income, expenses)
      const summaryResponse = await apiGet(`/api/summary?date=${selectedDate}`);
      const summary = await summaryResponse.json();
      console.log(`📊 Summary API Response for ${selectedDate}:`, {
        totalSales: summary.totalSales,
        totalOtherIncome: summary.totalOtherIncome,
        totalExpenses: summary.totalExpenses,
        counterBalance: summary.counterBalance,
        openingBalance: summary.openingBalance
      });
      setSummaryData(prev => ({
        ...prev,
        todaysSale: summary.totalSales || 0,
        totalIncome: summary.totalOtherIncome || 0,
        totalExpenses: summary.totalExpenses || 0
      }));
      
      // Always use previous day's closing balance as opening balance
      setOpeningBalance((summary.openingBalance || 0).toString());

    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Only allow positive numbers with up to 2 decimal places
    if (value === '' || /^\d+\.?\d{0,2}$/.test(value)) {
      setPayments(prev => ({
        ...prev,
        [name]: value
      }));
      setPaymentsModified(true);
    }
  };

  // Manual opening balance functions removed - now automatically uses previous day's closing balance

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const paymentData = {
        payment_date: selectedDate,
        cash_amount: parseFloat(payments.cash_amount) || 0,
        upi_amount: parseFloat(payments.upi_amount) || 0,
        card_amount: parseFloat(payments.card_amount) || 0
      };

      await apiPost('/api/payments', paymentData);
      setMessage('Payment record saved successfully!');
      setMessageType('success');
      setPaymentsModified(false); // Mark as saved
      loadData(); // Reload to show updated data
    } catch (error) {
      setMessage('Error saving payment record');
      setMessageType('error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
      <main className="w-full px-4 py-8 space-y-6 max-w-7xl mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 w-full">
          <div className="space-y-2 text-left">
            <h1 className="text-4xl font-bold text-left">Track Daily Payments</h1>
            <p className="text-muted-foreground text-left">Record cash, UPI, and card payments for the day</p>
          </div>
          
          {/* Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg border bg-card cursor-pointer hover:bg-accent transition-colors">
                <CalendarIcon className="h-5 w-5 text-primary" />
                <div className="text-left">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Business Date</div>
                  <div className="font-bold text-sm">
                    {selectedDate ? format(new Date(selectedDate), 'dd-MM-yyyy') : 'Pick a date'}
                  </div>
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={new Date(selectedDate)}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(format(date, 'yyyy-MM-dd'));
                  }
                }}
                disabled={(date) => date > new Date(currentBusinessDate)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Past Date Alert */}
        {isPastDate && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-blue-800">
                <CalendarIcon className="h-4 w-4" />
                <p className="text-sm font-medium">
                  You are viewing past data. Editing is disabled for historical records.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Wallet className="h-5 w-5 text-blue-600" />
                      <CardTitle className="text-base">Opening Counter Balance</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(parseFloat(openingBalance) || 0)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      Automatically set from previous day's closing balance
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <IndianRupee className="h-5 w-5 text-green-600" />
                      <CardTitle className="text-base">Today's Sale</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(summaryData.todaysSale)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment Methods</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">S.No</TableHead>
                    <TableHead>Mode of Payment</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-center">1.</TableCell>
                    <TableCell className="font-medium">Cash</TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        name="cash_amount"
                        value={payments.cash_amount}
                        onChange={handleInputChange}
                        onWheel={(e) => e.target.blur()}
                        placeholder="0.0"
                        onFocus={(e) => e.target.select()}
                        className="text-right"
                        disabled={isPastDate}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-center">2.</TableCell>
                    <TableCell className="font-medium">UPI</TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        name="upi_amount"
                        value={payments.upi_amount}
                        onChange={handleInputChange}
                        onWheel={(e) => e.target.blur()}
                        placeholder="0.0"
                        onFocus={(e) => e.target.select()}
                        className="text-right"
                        disabled={isPastDate}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-center">3.</TableCell>
                    <TableCell className="font-medium">Card</TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        name="card_amount"
                        value={payments.card_amount}
                        onChange={handleInputChange}
                        onWheel={(e) => e.target.blur()}
                        placeholder="0.0"
                        onFocus={(e) => e.target.select()}
                        className="text-right"
                        disabled={isPastDate}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2} className="font-bold">Total Amount</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(totalAmount)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </CardContent>
          </Card>

          <div className="flex flex-col items-center gap-4">
            <Button 
              type="submit" 
              disabled={loading || !paymentsModified || isPastDate}
              size="lg"
              className="w-full md:w-auto gap-2"
            >
              {loading ? (
                'Saving...'
              ) : isPastDate ? (
                'Past Date - View Only'
              ) : !paymentsModified ? (
                <>
                  <Check className="h-5 w-5" />
                  Already Saved
                </>
              ) : (
                'Save Payment Methods'
              )}
            </Button>

            <Card className="w-full border-2 border-primary">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold">Closing Counter Balance:</span>
                  <span className="text-2xl font-bold text-primary">
                    {formatCurrency(summaryData.closingBalance)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {message && (
              <Alert variant={messageType === 'success' ? 'default' : 'destructive'} className="w-full">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
          </div>
        </form>
    </main>
  );
}

export default TrackPayments;
